/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import TexturesManager from "App/utils/textures/textures_manager";
import TextureDataHandler from "App/utils/textures/texture_data_handler";
import {mat4, vec3, vec4} from "gl-matrix";
// @ts-ignore
import ResolutionComputeWorker from "App/workers/resolution_compute.worker";
import IScene from "js_interface/src/scene_interface";

export enum ResolveResolutionWorkerState {
    READY =   0,
    WORKING = 1
}

export default
class ResolveResolutionWorkerManager {

    private sceneAPI: IScene;
    private texturesManager: TexturesManager;
    private worker: Worker = null;
    private state: ResolveResolutionWorkerState = ResolveResolutionWorkerState.READY;
    // asynchronous actions in order to handle large amount of changes in batches
    private resourceMessages: any[] = [];
    private delayedRecomputeResolutionMsg: any = null;
    private resourceUpdateCB: () => void;
    private resourceUpdateInterval = 0;
    private resolutionUpdateCB: () => void;
    private resolutionUpdateInterval = 0;
    private recomputeTextureLODMessage: { projMatrixes: mat4[]; vpSizes: vec4[]; type: string; eyes: vec3[]; targets: vec3[] };

    constructor(aSceneAPI: IScene, aTexturesManager: TexturesManager) {
        this.sceneAPI = aSceneAPI;
        this.texturesManager = aTexturesManager;

        this.initWorker();

        this.resourceUpdateCB = () => {
            if (this.resourceMessages.length > 0) {
                this.worker.postMessage({type: 'resourceMessages', resourceMessages: this.resourceMessages});
                this.resourceMessages = [];
            }

            // stop the interval in case we have no request to resolutions recompute
            clearInterval(this.resourceUpdateInterval);
            this.resourceUpdateInterval = 0;
        };

        this.resolutionUpdateCB = () => {
            if (this.delayedRecomputeResolutionMsg) {
                if (this.state === ResolveResolutionWorkerState.READY) {
                    this.state = ResolveResolutionWorkerState.WORKING;
                    this.worker.postMessage(this.delayedRecomputeResolutionMsg);
                    this.delayedRecomputeResolutionMsg = null;
                }
            }
        };

        this.recomputeTextureLODMessage = {
            type: 'recomputeTextureLOD',
            eyes: [],
            targets: [],
            projMatrixes: [],
            vpSizes: []
        };
    }

    private initWorker() {
        // @ts-ignore
        this.worker = new ResolutionComputeWorker();
        this.createResolutionComputeThreadListener();
    }

    public scheduleDataUpdate() {
        if (!this.resourceUpdateInterval) {
            this.resourceUpdateInterval = window.setInterval(this.resourceUpdateCB, 10);
        }
    };

    public scheduleResolutionUpdate() {
        if (!this.resolutionUpdateInterval) {
            this.resolutionUpdateInterval = window.setInterval(this.resolutionUpdateCB, 50);
        }
    }

    public addTextureDataHandler(addTextureDataHandler: TextureDataHandler) {
        let dims = addTextureDataHandler.getAllDimensions();

        let message = {
            currentIndex: -1, // the current index is -1 when adding a new texture to the worker thread
            UID: addTextureDataHandler.getUID(),
            widths: dims.widths, // an array of values <1..n)
            heights: dims.heights, // an array of values <1..n)
            type: 'addTextureDataHandler'
        };

        this.resourceMessages.push(message);
        this.scheduleDataUpdate();
    }

    /***
     * Add/update shape in the resolution compute worker (we need current bbox of the shape and also the texture parameters
     * @param aTextureNode
     */
    public removeTextureDataHandler(aTextureDataHandler: TextureDataHandler) {
        let message = {
            UID: aTextureDataHandler.getUID(),
            type: 'removeTextureDataHandler' // and all shapes assigned to it respectively
        };

        this.resourceMessages.push(message);
        this.scheduleDataUpdate();
    }

    public attachShapeBBox(aTextureDataHandlerUID: number, aShapeUID: number, aBBoxMin: vec3 | number[], aBBoxMax: vec3 | number[]) {
        let message = {
            UID: aTextureDataHandlerUID,
            shapeUID: aShapeUID,
            bboxMin: aBBoxMin,
            bboxMax: aBBoxMax,
            type: 'attachShapeBBox'
        };

        this.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public updateShapeBBox(aTextureDataHandler: TextureDataHandler, aShapeUID: number, aBBoxMin: vec3, aBBoxMax: vec3) {
        let message = {
            UID: aTextureDataHandler,
            shapeUID: aShapeUID,
            bboxMin: aBBoxMin,
            bboxMax: aBBoxMax,
            type: 'updateShapeBBox'
        };

        this.resourceMessages.push(message);

        this.scheduleDataUpdate();

        this.recomputeTextureLOD();
    }

    public detachShapeBBox(aTextureDataHandler: TextureDataHandler, aShapeUID: number) {
        let message = {
            UID: aTextureDataHandler,
            shapeUID: aShapeUID,
            type: 'detachShapeBBox'
        };

        this.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public recomputeTextureLOD() {

        //collect all eyes, mvp and size of all views and send it to the resolution compute worker
        let viewMode = this.sceneAPI.viewModeModule;
        if (!viewMode) {
            return;
        }

        this.recomputeTextureLODMessage.eyes = [];
        this.recomputeTextureLODMessage.targets = [];
        this.recomputeTextureLODMessage.projMatrixes = [];
        this.recomputeTextureLODMessage.vpSizes = [];

        let views = viewMode.getViews();
        for (let i = 0; i < views.length; i++) {
            this.recomputeTextureLODMessage.eyes.push(views[i].getCamera().eye);
            this.recomputeTextureLODMessage.targets.push(views[i].getCamera().getCameraTarget());
            this.recomputeTextureLODMessage.projMatrixes.push(views[i].getCamera().getProjectionMatrixRef());
            this.recomputeTextureLODMessage.vpSizes.push(views[i].size);
        }

        this.delayedRecomputeResolutionMsg = this.recomputeTextureLODMessage;
        this.scheduleResolutionUpdate();
    }

    public limitTexturesResolution(aValue: number) {
        let message = {
            type: 'limitTexturesResolution',
            value: aValue
        };

        this.worker.postMessage(message);
    }

    public setViewFrustumCullingForTexturesSwitching(aValue: boolean) {
        let message = {
            type: 'setViewFrustumCullingForTexturesSwitching',
            value: aValue
        };

        this.worker.postMessage(message);
    }

    public createResolutionComputeThreadListener() {
        this.worker.addEventListener('message', (e: MessageEvent) => {
            let message = e.data;
            switch (message.type) {
                case 'recomputed':
                    this.texturesManager.switchPrioritiesRecomputed(message);
                    this.state = ResolveResolutionWorkerState.READY;
                    break;
                default:
            }
        }, false);
    }
}
